import { Controller } from "@hotwired/stimulus";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {
  getCurrentLocation,
  shouldShowLocationRequest,
} from "@/utils/location_utils";
import { GmpMap, MapBounds, BaseMarker } from "@/utils/map_interfaces";
import {
  waitForMap,
  boundsToUrlParams,
  createBoundsFromParams,
  updateUrlWithBounds,
  initializeMarkerClusterer,
} from "@/utils/map_utils";
import { renderBoundary, fitMapToBoundary } from "@/utils/map_boundary_utils";
import { waitFor } from "@/utils/time";

export default class extends Controller {
  static targets = ["map", "searchButton"];
  static values = {
    markersUrl: String,
    defaultLat: { type: Number, default: 39.8283 },
    defaultLng: { type: Number, default: -98.5795 },
    boundary: { type: Object, default: null },
    useCurrentLocation: { type: Boolean, default: true },
  };

  declare readonly mapTarget: GmpMap;
  declare readonly searchButtonTarget?: HTMLButtonElement;
  declare readonly markersUrlValue: string;
  declare readonly defaultLatValue: number;
  declare readonly defaultLngValue: number;
  declare readonly boundaryValue: any;
  declare readonly useCurrentLocationValue: boolean;
  declare readonly hasSearchButtonTarget: boolean;

  protected map: google.maps.Map | null = null;
  protected markers: google.maps.Marker[] = [];
  protected markerClusterer: MarkerClusterer | null = null;
  protected mapMoved: boolean = false;
  protected boundaryLayer: google.maps.Data | null = null;

  async connect() {
    await waitForMap(this.mapTarget);
    this.map = this.mapTarget.innerMap;
    this.map.set("disableDefaultUI", true);

    this.markerClusterer = initializeMarkerClusterer(this.map);
    await waitFor(() => this.markerClusterer);

    const urlParams = new URLSearchParams(window.location.search);

    if (this.boundaryValue) {
      this.boundaryLayer = renderBoundary(this.map, this.boundaryValue);

      if (!urlParams.has("ne_lat")) {
        fitMapToBoundary(this.map, this.boundaryLayer);
      }
    }

    if (urlParams.has("ne_lat")) {
      await this.restoreMapFromUrl(urlParams);
    } else if (this.boundaryLayer) {
      await this.fetchMarkersForCurrentBounds();
    } else if (this.useCurrentLocationValue) {
      await this.initializeUserLocation();
    } else {
      await this.fetchMarkers();
    }

    this.setupMapListeners();
  }

  // Public method to expose current map bounds for other controllers
  getBounds(): MapBounds | null {
    if (!this.map) return null

    const bounds = this.map.getBounds()
    return bounds ? boundsToUrlParams(bounds) : null;
  }

  protected async initializeUserLocation() {
    if (!this.map) return;
    if (!this.useCurrentLocationValue) {
      return;
    }

    try {
      if (!shouldShowLocationRequest()) {
        throw new Error("Location request declined");
      }

      const coordinates = await getCurrentLocation(
        this.defaultLatValue,
        this.defaultLngValue,
      );
      this.map.setCenter({
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      });
      this.map.setZoom(11);
    } catch (error) {
      console.warn("Using default location:", error);
      this.map.setCenter({
        lat: this.defaultLatValue,
        lng: this.defaultLngValue,
      });
      this.map.setZoom(4);
    }

    await this.fetchMarkersForCurrentBounds();
  }

  protected setupMapListeners() {
    if (!this.map) return;

    ["dragend", "zoom_changed"].forEach((event) => {
      google.maps.event.addListener(this.map!, event, () => {
        this.mapMoved = true;
        this.showSearchButton();
      });
    });
  }

  protected showSearchButton() {
    if (this.mapMoved && this.hasSearchButtonTarget) {
      this.searchButtonTarget.classList.remove("hidden");
    }
  }

  searchArea() {
    this.mapMoved = false;
    if (this.hasSearchButtonTarget) {
      this.searchButtonTarget.classList.add("hidden");
    }
    this.updateMapState();
  }

  protected async updateMapState() {
    if (!this.map) return;
    const bounds = this.map.getBounds();
    if (!bounds) return;

    const boundsParams = boundsToUrlParams(bounds);
    updateUrlWithBounds(boundsParams);
    await this.fetchMarkers(boundsParams);
  }

  protected async restoreMapFromUrl(params: URLSearchParams) {
    if (!this.map) return;

    const boundsParams: MapBounds = {
      ne_lat: parseFloat(params.get("ne_lat")!),
      ne_lng: parseFloat(params.get("ne_lng")!),
      sw_lat: parseFloat(params.get("sw_lat")!),
      sw_lng: parseFloat(params.get("sw_lng")!),
    };

    const bounds = createBoundsFromParams(boundsParams);
    this.map.fitBounds(bounds);
    await this.fetchMarkers(boundsParams);
  }

  protected async fetchMarkersForCurrentBounds() {
    if (!this.map) return;
    await waitFor(() => this.map?.getBounds());
    const bounds = this.map.getBounds();
    if (!bounds) return;
    await this.fetchMarkers(boundsToUrlParams(bounds));
  }

  protected async fetchMarkers(bounds?: MapBounds) {
    const url = new URL(this.markersUrlValue, window.location.origin);

    if (bounds) {
      Object.entries(bounds).forEach(([key, value]) => {
        url.searchParams.set(key, value.toString());
      });
    }

    const response = await fetch(url);
    const markers = await response.json();

    this.clearMarkers();
    this.renderMarkers(markers);
  }

  protected clearMarkers() {
    this.markerClusterer?.clearMarkers();
    this.markers.forEach((marker) => marker.setMap(null));
    this.markers = [];
  }

  protected renderMarkers<T extends BaseMarker>(markers: T[]) {
    if (!this.map) return;

    this.markers = markers.map((markerData) => {
      const marker = this.createMarker(markerData);

      marker.addListener("click", this.handleMarkerClick(markerData));

      return marker;
    });

    if (this.markers.length > 1500) {
      this.markerClusterer.addMarkers(this.markers);
    }
  }

  protected createMarker<T extends BaseMarker>(
    markerData: T,
  ): google.maps.Marker {
    return new google.maps.Marker({
      position: this.offsetMarkerPosition(markerData),
      map: this.map!,
      icon: this.createMarkerIcon(markerData),
    });
  }

  protected createMarkerIcon<T extends BaseMarker>(
    markerData: T,
  ): google.maps.Icon {
    // To be implemented by child classes
    return {
      url: "",
      scaledSize: new google.maps.Size(10, 10),
      anchor: new google.maps.Point(5, 5),
    };
  }

  protected offsetMarkerPosition<T extends BaseMarker>(
    markerData: T,
  ): google.maps.LatLng {
    return new google.maps.LatLng(
      markerData.latitude + ((markerData.id % 10) / 200000 - 0.000025),
      markerData.longitude + (((markerData.id * 7) % 10) / 200000 - 0.000025),
    );
  }

  protected handleMarkerClick<T extends BaseMarker>(markerData: T): () => void {
    return () => {
      // To be implemented by child classes
    };
  }

  protected createSvgUrl(content: string): string {
    return `data:image/svg+xml;base64,${btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15">
        ${content}
      </svg>
    `)}`;
  }

  disconnect() {
    this.clearMarkers();
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
      this.markerClusterer = null;
    }
    if (this.boundaryLayer) {
      this.boundaryLayer.setMap(null);
      this.boundaryLayer = null;
    }
  }
}
